import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import AdminLayout from '../components/AdminLayout';
import {
  Button, Card, Input, Modal, EmptyState, Skeleton, StatusPill, useToast,
} from '../../design-system';
import '../styles/AdminIntegrations.css';

// Super-admin management of the bearer credentials that open
// /api/v1/integration — the read-only client API other applications (Lexform
// today) pull from.
//
// ⚠️ THE KEY IS SHOWN ONCE, HERE, AND NOWHERE ELSE EVER AGAIN. The server keeps
// only a hash, so this dialog is not a convenience that could be reopened later
// — there is nothing left to reopen. The copy says so before the key is
// generated, not after.
//
// ⚠️ "EVERY ORGANIZATION" IS A SEPARATE, DELIBERATE CHOICE, never the result of
// picking nothing. The server refuses an empty list rather than reading it as
// unrestricted, and this form mirrors that: the radio has to be moved.

const dateLabel = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const usedLabel = (value) => {
  if (!value) return 'Never used';
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days === 0) return 'Used today';
  if (days === 1) return 'Used yesterday';
  if (days < 30) return `Used ${days} days ago`;
  return `Used ${dateLabel(value)}`;
};

/** The scope picker, shared by the create dialog and the edit one. */
function ScopeFields({ scope, setScope, picked, setPicked, organizations }) {
  const toggle = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="ai-scope">
      <label className={`ai-scope-option ${scope === 'listed' ? 'is-on' : ''}`}>
        <input type="radio" checked={scope === 'listed'} onChange={() => setScope('listed')} />
        <span>
          <strong>Chosen organizations</strong>
          <small>The key reads only the firms ticked below.</small>
        </span>
      </label>

      {scope === 'listed' && (
        <div className="ai-org-list">
          {organizations.map((org) => (
            <label key={org.id} className="ai-org">
              <input
                type="checkbox"
                checked={picked.includes(org.id)}
                onChange={() => toggle(org.id)}
              />
              <span className="ai-org-name">{org.name}</span>
              {/* Stated rather than hidden: a key may be scoped to a firm that
                  is currently closed, and it will read nothing until that is
                  fixed. Hiding the row would make that look like a bug here. */}
              {!org.available && <span className="ai-org-flag">no active subscription</span>}
            </label>
          ))}
        </div>
      )}

      <label className={`ai-scope-option ${scope === 'all' ? 'is-on' : ''}`}>
        <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} />
        <span>
          <strong>Every organization</strong>
          <small>
            Reads every firm on this deployment, <em>including ones that register later</em>. Give
            this only to an integration you operate yourself.
          </small>
        </span>
      </label>
    </div>
  );
}

function AdminIntegrations() {
  const [keys, setKeys] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [environment, setEnvironment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [scope, setScope] = useState('listed');
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  // The one-time reveal. Held in state and dropped on close — never refetched.
  const [revealed, setRevealed] = useState(null);

  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/integration-keys');
      setKeys(res.data.keys || []);
      setOrganizations(res.data.organizations || []);
      setEnvironment(res.data.environment || []);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load integration keys');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const orgName = useMemo(() => {
    const map = new Map(organizations.map((o) => [o.id, o.name]));
    return (id) => map.get(id) || 'Unknown organization';
  }, [organizations]);

  const openCreate = () => {
    setLabel('');
    setNote('');
    setScope('listed');
    setPicked([]);
    setCreating(true);
  };

  const openEdit = (key) => {
    setLabel(key.label);
    setNote(key.note || '');
    setScope(key.scope);
    setPicked(key.organizationIds);
    setEditing(key);
  };

  const closeForms = () => {
    setCreating(false);
    setEditing(null);
  };

  const submitCreate = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post('/api/integration-keys', {
        label, note, scope, organizationIds: picked,
      });
      setKeys((prev) => [res.data.key, ...prev]);
      closeForms();
      setRevealed({ key: res.data.key, plaintext: res.data.plaintext, rotated: false });
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not issue the key');
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      const res = await apiClient.patch(`/api/integration-keys/${editing.id}`, {
        label, note, scope, organizationIds: picked,
      });
      setKeys((prev) => prev.map((k) => (k.id === editing.id ? res.data.key : k)));
      closeForms();
      toast.success('Key updated');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not update the key');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (key, isActive) => {
    if (
      !isActive &&
      !window.confirm(
        `Revoke "${key.label}"? Anything still using this key starts failing immediately. `
          + 'You can switch it back on.'
      )
    ) return;

    setBusyId(key.id);
    try {
      const res = await apiClient.patch(`/api/integration-keys/${key.id}`, { isActive });
      setKeys((prev) => prev.map((k) => (k.id === key.id ? res.data.key : k)));
      toast.success(isActive ? 'Key restored' : 'Key revoked');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not update the key');
    } finally {
      setBusyId(null);
    }
  };

  const rotate = async (key) => {
    if (
      !window.confirm(
        `Rotate "${key.label}"? A new key is generated and the current one stops working at once — `
          + 'the integration holding it will fail until you give it the new value.'
      )
    ) return;

    setBusyId(key.id);
    try {
      const res = await apiClient.post(`/api/integration-keys/${key.id}/rotate`);
      setKeys((prev) => prev.map((k) => (k.id === key.id ? res.data.key : k)));
      setRevealed({ key: res.data.key, plaintext: res.data.plaintext, rotated: true });
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not rotate the key');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete "${key.label}" permanently? Its audit history stays.`)) return;
    setBusyId(key.id);
    try {
      await apiClient.delete(`/api/integration-keys/${key.id}`);
      setKeys((prev) => prev.filter((k) => k.id !== key.id));
      toast.success('Key deleted');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not delete the key');
    } finally {
      setBusyId(null);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(revealed.plaintext);
      toast.success('Key copied');
    } catch {
      // Clipboard access can be refused; the value is on screen to select.
      toast.info('Select the key and copy it manually');
    }
  };

  const formValid = label.trim() && (scope === 'all' || picked.length > 0);

  return (
    <AdminLayout
      title="Integrations"
      subtitle="Keys for the read-only client API at /api/v1/integration"
      headerRight={<Button onClick={openCreate}>Issue a key</Button>}
    >
      <div className="ai-page">
        {environment.length > 0 && (
          <Card className="ai-legacy">
            <strong>
              {environment.length} key{environment.length > 1 ? 's' : ''} configured in the server
              environment
            </strong>
            <p>
              {environment.map((e) => e.label).join(', ')} — scoped to{' '}
              {environment.map((e) => (e.scope === 'all' ? 'every organization' : `${e.organizationIds.length} organization(s)`)).join(', ')}.
              {' '}These cannot be scoped or revoked from here; they need an edit on the server and a
              restart. Issue a replacement above, give the new value to whoever holds the old one,
              then remove <code>LEXFORM_INTEGRATION_*</code> from the environment.
            </p>
          </Card>
        )}

        {loading ? (
          <Card><Skeleton.Text lines={6} /></Card>
        ) : keys.length === 0 ? (
          <EmptyState
            icon="🔑"
            title="No keys issued"
            description="Issue one to let another application read this platform's client records."
            action={<Button onClick={openCreate}>Issue a key</Button>}
          />
        ) : (
          <div className="ai-list">
            {keys.map((key) => (
              <Card key={key.id} className={`ai-key ${key.isActive ? '' : 'is-revoked'}`}>
                <div className="ai-key-main">
                  <div className="ai-key-head">
                    <span className="ai-key-label">{key.label}</span>
                    <StatusPill status={key.isActive ? 'active' : 'revoked'} />
                    <code className="ai-key-hint">omk_…{key.keyHint}</code>
                  </div>

                  <div className="ai-key-scope">
                    {key.scope === 'all' ? (
                      <span className="ai-scope-all">Every organization</span>
                    ) : (
                      key.organizationIds.map((id) => (
                        <span key={id} className="ai-scope-chip">{orgName(id)}</span>
                      ))
                    )}
                  </div>

                  {key.note && <p className="ai-key-note">{key.note}</p>}

                  <div className="ai-key-meta">
                    <span>{usedLabel(key.lastUsedAt)}</span>
                    <span>Issued {dateLabel(key.createdAt)}{key.createdByName ? ` by ${key.createdByName}` : ''}</span>
                  </div>
                </div>

                <div className="ai-key-actions">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(key)} disabled={busyId === key.id}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => rotate(key)} disabled={busyId === key.id}>
                    Rotate
                  </Button>
                  {key.isActive ? (
                    <Button variant="ghost" size="sm" onClick={() => setActive(key, false)} disabled={busyId === key.id}>
                      Revoke
                    </Button>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setActive(key, true)} disabled={busyId === key.id}>
                        Restore
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(key)} disabled={busyId === key.id}>
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={creating || Boolean(editing)}
        onClose={closeForms}
        title={editing ? `Edit ${editing.label}` : 'Issue an integration key'}
        footer={
          <>
            <Button variant="secondary" onClick={closeForms} disabled={saving}>Cancel</Button>
            <Button onClick={editing ? submitEdit : submitCreate} disabled={!formValid || saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Issue key'}
            </Button>
          </>
        }
      >
        <div className="ai-form">
          <Input
            label="Label"
            placeholder="Lexform"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText="Appears in the audit trail. Not secret."
          />
          <Input
            label="Note (optional)"
            placeholder="Who holds this and why"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="ai-form-section">
            <span className="ai-form-legend">What may this key read?</span>
            <ScopeFields
              scope={scope}
              setScope={setScope}
              picked={picked}
              setPicked={setPicked}
              organizations={organizations}
            />
          </div>

          {!editing && (
            <p className="ai-form-warning">
              The key is shown once, on the next screen. It is stored as a hash, so it cannot be
              looked up again — if it is lost, rotate it and hand out a new one.
            </p>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(revealed)}
        onClose={() => setRevealed(null)}
        title={revealed?.rotated ? 'New key' : 'Key issued'}
        footer={<Button onClick={() => setRevealed(null)}>Done</Button>}
      >
        {revealed && (
          <div className="ai-reveal">
            <p className="ai-reveal-warning">
              <strong>Copy this now.</strong> It is not stored anywhere and cannot be shown again.
              {revealed.rotated && ' The previous key has already stopped working.'}
            </p>
            <code className="ai-reveal-key">{revealed.plaintext}</code>
            <Button variant="secondary" onClick={copyKey}>Copy key</Button>
            <p className="ai-reveal-hint">
              Send it as <code>Authorization: Bearer …</code> against{' '}
              <code>/api/v1/integration</code>.
              {revealed.key.scope === 'all'
                ? ' It reads every organization, so the caller must send ?organizationId= on client requests.'
                : revealed.key.organizationIds.length > 1
                  ? ' It reads more than one organization, so the caller must send ?organizationId= on client requests.'
                  : ' It reads one organization, so no ?organizationId= is needed.'}
            </p>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}

export default AdminIntegrations;
